import React, { useEffect } from 'react';
import { WidgetWrapper } from '../../../../components/WidgetWrapper/WidgetWrapper';
import { EditField } from '../../../../components/EditPanel/types';
import { useWidgetEditable } from '../../../../hooks/useWidgetEditable';
import { themeColor } from '../../../../types/color';
import { ConciergeHomeGreetingState, GreetingIconPosition } from './types';

/**
 * Load a Google Font stylesheet into document.head.
 * This is necessary because fonts loaded inside Shadow DOM don't register
 * @font-face declarations globally, causing the font to not render.
 */
const loadGoogleFontInHead = (fontUrl: string): void => {
  const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
  if (existingLink) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
};

/**
 * Default starburst/sparkle SVG icon used when no brand icon is uploaded.
 * Sourced from the Figma design (A7 preset).
 */
const DefaultIconSvg: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M48 22.8232H29.0836L46.1268 14.4962L45.1182 12.3764L28.0749 20.7034L39.8703 5.69848L38.0548 4.23074L26.2622 19.2357L30.4697 0.52336L28.2104 0L24 18.7123L19.7896 0L17.5274 0.52336L21.7378 19.2357L9.94236 4.23074L8.12968 5.69848L19.9222 20.7034L2.87896 12.3764L1.8732 14.4962L18.9164 22.8232H0V25.1768H18.9164L1.8732 33.5038L2.87896 35.6265L19.9222 27.2995L8.12968 42.3044L9.94236 43.7722L21.7378 28.7673L17.5274 47.4766L19.7896 48L24 29.2906L28.2104 48L30.4697 47.4766L26.2622 28.7673L38.0548 43.7722L39.8703 42.3044L28.0749 27.2995L45.1182 35.6265L46.1268 33.5038L29.0836 25.1768H48V22.8232Z"
      fill={color}
    />
  </svg>
);

const defaultState: ConciergeHomeGreetingState = {
  greetingTemplate: 'Hi, {name}.',
  loggedOutGreeting: 'Hello',

  greetingFontSize: 24,
  greetingFontWeight: 'semibold',
  greetingFontFamily: 'inherit',
  greetingColor: themeColor('textPrimary'),
  showUserName: true,

  iconType: 'brand-icon',
  emoji: '\uD83D\uDC4B',
  brandIcon: '',
  iconSize: 32,
  iconColor: themeColor('primary'),
  iconRotationAnimation: true,
  iconRotationDuration: 8,

  iconMarginBottom: 8,
  greetingMarginBottom: 16,
  mobile_greetingMarginBottom: 8,

  mobile_greetingFontSize: 18,
  mobile_iconSize: 28,
};

const editFields: EditField[] = [
  // Greeting text
  {
    key: 'greetingTemplate',
    label: 'Greeting Text',
    type: 'text',
    group: 'Greeting Text',
  },
  {
    key: 'showUserName',
    label: 'Show User Name',
    type: 'toggle',
    group: 'Greeting Text',
  },
  {
    key: 'greetingFontSize',
    label: 'Font Size',
    type: 'number',
    min: 16,
    max: 40,
    step: 2,
    group: 'Greeting Text',
    responsive: true,
  },
  {
    key: 'greetingFontWeight',
    label: 'Font Weight',
    type: 'options',
    options: [
      { label: 'Normal', value: 'normal' },
      { label: 'Medium', value: 'medium' },
      { label: 'Semibold', value: 'semibold' },
      { label: 'Bold', value: 'bold' },
    ],
    group: 'Greeting Text',
  },
  {
    key: 'greetingFontFamily',
    label: 'Font Family',
    type: 'font',
    group: 'Greeting Text',
  },
  {
    key: 'greetingColor',
    label: 'Color',
    type: 'color',
    group: 'Greeting Text',
  },
  {
    key: 'greetingMarginBottom',
    label: 'Spacing Below',
    type: 'number',
    min: 0,
    max: 48,
    step: 4,
    group: 'Greeting Text',
    responsive: true,
  },

  // Icon
  {
    key: 'iconType',
    label: 'Icon Type',
    type: 'options',
    options: [
      { label: 'Brand Icon', value: 'brand-icon' },
      { label: 'Emoji', value: 'emoji' },
    ],
    group: 'Greeting Icon',
  },
  {
    key: 'emoji',
    label: 'Emoji',
    type: 'text',
    group: 'Greeting Icon',
    condition: { field: 'iconType', value: 'emoji' },
  },
  {
    key: 'brandIcon',
    label: 'Icon',
    type: 'icon',
    group: 'Greeting Icon',
    condition: { field: 'iconType', value: 'brand-icon' },
  },
  {
    key: 'iconSize',
    label: 'Icon Size',
    type: 'number',
    min: 16,
    max: 64,
    step: 4,
    group: 'Greeting Icon',
    responsive: true,
  },
  {
    key: 'iconColor',
    label: 'Icon Color',
    type: 'color',
    group: 'Greeting Icon',
    condition: { field: 'iconType', value: 'brand-icon' },
  },
  {
    key: 'iconRotationAnimation',
    label: 'Rotation Animation',
    type: 'toggle',
    group: 'Greeting Icon',
  },
  {
    key: 'iconRotationDuration',
    label: 'Rotation Speed (seconds)',
    type: 'number',
    min: 1,
    max: 30,
    step: 1,
    group: 'Greeting Icon',
    condition: 'iconRotationAnimation',
  },
  {
    key: 'iconMarginBottom',
    label: 'Icon Spacing Below',
    type: 'number',
    min: 0,
    max: 24,
    step: 4,
    group: 'Greeting Icon',
  },
];

interface ConciergeHomeGreetingWidgetProps {
  widgetId?: string;
  displayName?: string;
  initialState?: Partial<ConciergeHomeGreetingState>;
  userName?: string;
  showIcon?: boolean;
  iconPosition?: GreetingIconPosition;
}

export const ConciergeHomeGreetingWidget: React.FC<ConciergeHomeGreetingWidgetProps> = ({
  widgetId = 'concierge-home-greeting',
  displayName = 'Greeting',
  initialState = {},
  userName,
  showIcon = false,
  iconPosition = 'above',
}) => {
  const {
    state,
    isLoading,
    isSaving,
    handleEditChange,
    handleSave,
    resetField,
    savedKeys,
    themeColors,
    themeFonts,
    themeIcons,
    resolveColor,
    canSave,
  } = useWidgetEditable<ConciergeHomeGreetingState>(widgetId, { ...defaultState, ...initialState }, editFields);

  // Resolve font family — when "inherit", fall back to the brand's default font
  const fontKey = state.greetingFontFamily;
  const fontData =
    fontKey && fontKey !== 'inherit'
      ? themeFonts[fontKey]
      : themeFonts['studio-brand-font'] ?? Object.values(themeFonts)[0] ?? null;

  const googleFontLink = fontData && fontData.format === 'google' ? fontData.url : null;

  useEffect(() => {
    if (googleFontLink) {
      loadGoogleFontInHead(googleFontLink);
    }
  }, [googleFontLink]);

  if (isLoading) {
    return <></>;
  }

  const fontFamilyCss = fontData
    ? fontData.format === 'system'
      ? fontData.url
      : `"${fontData.name}", sans-serif`
    : 'inherit';

  const needsFontFace = fontData && fontData.format !== 'system' && fontData.format !== 'google';

  const uniqueClass = `greeting-${widgetId || 'default'}`;

  const mobileGreetingFontSize = (state as any).mobile_greetingFontSize ?? state.greetingFontSize;
  const mobileIconSize = (state as any).mobile_iconSize ?? state.iconSize;
  const mobileGreetingMarginBottom = state.mobile_greetingMarginBottom ?? state.greetingMarginBottom;

  const fontStyleCss = `
    ${
      needsFontFace
        ? `
      @font-face {
        font-family: "${fontData.name}";
        src: url("${fontData.url}") format("${fontData.format}");
        font-display: swap;
      }
    `
        : ''
    }
    ${fontFamilyCss !== 'inherit' ? `.${uniqueClass} { font-family: ${fontFamilyCss} !important; }` : ''}
    .${uniqueClass}-wrap {
      margin-bottom: ${state.greetingMarginBottom}px;
    }
    @media (max-width: 767px) {
      .${uniqueClass}-wrap {
        margin-bottom: ${mobileGreetingMarginBottom}px !important;
      }
      .${uniqueClass}-text {
        font-size: ${mobileGreetingFontSize}px !important;
        line-height: 26px !important;
        letter-spacing: 0.08px !important;
      }
      .${uniqueClass}-icon {
        width: ${mobileIconSize}px !important;
        height: ${mobileIconSize}px !important;
      }
      .${uniqueClass}-icon svg {
        width: ${mobileIconSize}px !important;
        height: ${mobileIconSize}px !important;
      }
    }
  `.trim();

  // Resolve greeting text with user name
  let greetingText: string;
  if (state.showUserName && userName) {
    greetingText = state.greetingTemplate.replace('{name}', userName);
  } else {
    greetingText = state.loggedOutGreeting || 'Hello';
  }

  const fontWeight =
    state.greetingFontWeight === 'normal'
      ? 400
      : state.greetingFontWeight === 'medium'
        ? 500
        : state.greetingFontWeight === 'semibold'
          ? 600
          : 700;

  // Resolve icon color
  const resolvedIconColor = resolveColor(state.iconColor);

  // Rotation animation CSS
  const rotationAnimId = `greeting-icon-spin-${widgetId || 'default'}`;
  const rotationCss = state.iconRotationAnimation
    ? `@keyframes ${rotationAnimId} { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`
    : '';
  const rotationStyle: React.CSSProperties = state.iconRotationAnimation
    ? {
        animation: `${rotationAnimId} ${state.iconRotationDuration}s linear infinite`,
      }
    : {};

  // Render the icon element
  const renderIcon = () => {
    if (!showIcon) return null;

    let iconElement: React.ReactNode = null;

    if (state.iconType === 'emoji') {
      iconElement = <span style={{ fontSize: state.iconSize }}>{state.emoji}</span>;
    } else if (state.brandIcon && themeIcons[state.brandIcon]) {
      // Use uploaded theme icon with color tinting via mask-image
      iconElement = (
        <div
          style={{
            width: state.iconSize,
            height: state.iconSize,
            backgroundColor: resolvedIconColor,
            WebkitMaskImage: `url(${themeIcons[state.brandIcon].url})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${themeIcons[state.brandIcon].url})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
          }}
        />
      );
    } else {
      // Default starburst SVG icon
      iconElement = <DefaultIconSvg size={state.iconSize} color={resolvedIconColor} />;
    }

    return (
      <div
        className={`${uniqueClass}-icon`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...rotationStyle,
        }}
      >
        {iconElement}
      </div>
    );
  };

  const isInline = iconPosition === 'inline';

  const content = (
    <>
      <style>{fontStyleCss}</style>
      {rotationCss && <style>{rotationCss}</style>}
      <div
        className={`${uniqueClass}-wrap${fontFamilyCss !== 'inherit' ? ` ${uniqueClass}` : ''}`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        {/* Icon above greeting (A7 style) */}
        {showIcon && !isInline && <div style={{ marginBottom: state.iconMarginBottom }}>{renderIcon()}</div>}

        {/* Greeting text (with optional inline icon for A6 style) */}
        <span
          className={`${uniqueClass}-text`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: isInline && showIcon ? 8 : 0,
            fontSize: state.greetingFontSize,
            fontWeight,
            color: resolveColor(state.greetingColor),
            lineHeight: 1.2,
          }}
        >
          {showIcon && isInline && renderIcon()}
          {greetingText}
        </span>
      </div>
    </>
  );

  return (
    <WidgetWrapper
      widgetId={widgetId}
      displayName={displayName}
      editFields={editFields}
      editValues={state}
      onEditChange={handleEditChange}
      onSave={canSave ? handleSave : undefined}
      isSaving={isSaving}
      savedKeys={savedKeys}
      onResetField={resetField}
      themeColors={themeColors}
      themeFonts={themeFonts}
      themeIcons={themeIcons}
    >
      {content}
    </WidgetWrapper>
  );
};
